<template>
  <div v-if="available" class="workflow-chat-entry floating" :class="{ 'is-mobile': isMobile }">
    <button v-if="isMobile" type="button" class="mobile-workflow-entry" :disabled="opening" :aria-busy="String(opening)" @click.stop="openDiscussion"><ChatNavigationIcon name="group" /><span>{{ $t('workflowChat.entry') }}</span></button>
    <el-button v-else type="primary" plain icon="el-icon-chat-dot-round" :loading="opening" @click="openDiscussion">{{ $t("workflowChat.entry") }}</el-button>
    <AgentServiceWindow v-if="!useWorkbench" :show="show" :title="title" :subtitle="$t('workflowChat.agentManager')" :initial="$t('workflowChat.groupInitial')" :show-presence="false" :label="title" @update:show="show = $event">
      <p v-if="opening" class="entry-state" role="status">{{ $t("agentServiceUI.connecting") }}</p>
      <div v-else-if="error" class="entry-state" role="alert">
        <p>{{ error }}</p>
        <el-button type="text" @click="openDiscussion">{{ $t("agentServiceUI.retry") }}</el-button>
      </div>
      <WorkflowChatWorkspace v-else-if="groupId" :key="version" conversation-only :group-id="groupId" @private-chat="openPrivateChat" @title-change="businessNumber = $event" />
    </AgentServiceWindow>
  </div>
</template>
<script>
import { workflowConversationTitle } from "@/services/workflow-chat-inbox";
import ChatNavigationIcon from '@/components/chat-workbench/ChatNavigationIcon.vue';
import { Toast } from 'vant';
import AgentServiceWindow from "@/components/agent-service/AgentServiceWindow.vue";
import WorkflowChatWorkspace from "./WorkflowChatWorkspace.vue";
import { workflowChatAvailable, workflowChatOptions, enterWorkflowChat } from "@/api/workflowChat";
import { getContractDraft } from "@/api/fastAccess";
import { workflowBusinessForRoute, workflowReviewTypeForRoute } from "@/services/workflow-chat";

export default {
  name: "WorkflowChatEntry",
  components: { AgentServiceWindow, WorkflowChatWorkspace, ChatNavigationIcon },
  props: { inbox: { type: Boolean, default: false } },
  data() { return { available: false, show: false, version: 0, opening: false, groupId: "", businessNumber: "", error: "" }; },
  computed: {
    isMobile() { return !/^\/pc(?:\/|$)/u.test(this.$route.path); },
    useWorkbench() { return Boolean(this.$store.state.chatWorkbench); },
    identity() { return String(this.$store.state.user?.userInfo?.token || ""); },
    business() { return this.inbox ? null : workflowBusinessForRoute(this.$route); },
    contextKey() { return JSON.stringify([this.identity, this.business, this.$route.path, this.$route.query]); },
    title() { return this.businessNumber || this.$t("workflowChat.discussion"); },
  },
  watch: { contextKey: { immediate: true, handler() { this.load(); } } },
  beforeDestroy() { this.version++; },
  methods: {
    async load() {
      const version = ++this.version;
      this.available = false; this.show = false; this.groupId = ""; this.opening = false; this.error = ""; this.businessNumber = "";
      if (!this.identity || !this.business) return;
      try {
        const enabled = await workflowChatAvailable();
        if (version === this.version) this.available = Boolean(enabled);
      } catch { /* 旧服务未启用附加协作能力时不干扰原订单页面。 */ }
    },
    async openDiscussion() {
      if (this.opening || !this.available) return;
      const version = this.version;
      this.show = true; this.opening = true; this.error = ""; this.groupId = "";
      try {
        let business = this.business;
        if (business.businessType === "CONTRACT_DRAFT") {
          const result = await getContractDraft({ goodsCode: business.goodsCode, preOrderId: business.preOrderId });
          if (Number(result?.code) !== 200 || !/^[1-9]\d*$/.test(String(result.data?.contractId || ""))) throw new Error(this.$t("workflowChat.draftUnavailable"));
          business = { businessType: "CONTRACT", businessId: String(result.data.contractId) };
        }
        if (version !== this.version) return;
        const options = await workflowChatOptions(business);
        if (version !== this.version) return;
        const reviewType = workflowReviewTypeForRoute(this.$route);
        const installment = this.$route.query.installmentNo;
        const matches = options.filter(option => Number(option.scope.reviewType) === reviewType && (installment == null || Number(option.scope.installmentNo) === Number(installment)));
        if (matches.length !== 1) throw new Error(this.$t("workflowChat.scopeUnavailable"));
        const detail = await enterWorkflowChat(matches[0].scope);
        if (version !== this.version) return;
        this.groupId = detail.group.id; this.businessNumber = workflowConversationTitle(detail, (key, params) => this.$t(key, params));
        if (this.useWorkbench) {
          this.$store.commit('chatWorkbench/open', { kind: 'WORKFLOW', groupId: String(this.groupId), title: this.businessNumber });
          this.show = false;
        }
      } catch (error) {
        if (version === this.version) {
          this.error = error.message || this.$t("messageCenter.conversationsFailed");
          if (this.useWorkbench) { if (this.isMobile) Toast.fail(this.error); else this.$message?.error(this.error); }
        }
      } finally { if (version === this.version) this.opening = false; }
    },
    openPrivateChat(member) {
      if (this.useWorkbench) { this.$store.commit('chatWorkbench/open', { kind: 'PRIVATE', member }); return; }
      this.show = false;
      const path = this.$route.path.startsWith("/pc/") ? "/pc/myxg/messageCenter" : "/myxg/messageCenter";
      this.$router.push({ path, query: { peerUserId: String(member.userId), peerName: member.displayName } });
    },
  },
};
</script>
<style lang="less" scoped>
.workflow-chat-entry {
  &.is-mobile {
    .mobile-workflow-entry { display: inline-flex; align-items: center; gap: 8px; min-height: 44px; padding: 8px 12px; border: 1px solid #dde3ee; border-radius: 12px; background: #fff; color: #246de0; font: inherit; white-space: nowrap; &:disabled { opacity: .5; } &:focus-visible { outline: 2px solid #4286f5; outline-offset: 2px; } }
  }
  .el-button { min-height: 44px; }
  &.floating { position: fixed; right: 24px; bottom: 100px; z-index: 1900; }
  .entry-state { padding: 16px; color: #667085; }
  @media (max-width: 640px) { &.floating { right: 12px; bottom: calc(92px + env(safe-area-inset-bottom)); } }
}
</style>
